import { useOrganization, useUser } from '@clerk/clerk-react';
import { ProjectsProvider } from '@flobi/asset-library/projects';
import '@flobi/asset-library/share.css';
import '@flobi/asset-library/projects.css';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { myUserId } from '../live/collab';
import { useTheme } from '../theme';
import { openProjectShare } from '../share/artworkShare';
import { artworkProjectsAdapter, newProjectInProjectsUrl, projectInProjectsUrl, projectPictures } from './projectsApi';

/** Project settings get Artwork's part: the project's file covers as cover choices, and the project's Share for "+ Add people". */
const SETTINGS = {
  coverChoices: (projectId: string) => projectPictures(projectId),
  onShare: (project: { id: string; name: string }) => openProjectShare({ id: project.id, name: project.name }),
};

const openTab = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

/**
 * Projects for every signed-in Artwork page (PROJECTS-MODEL.md W3): the shared kit's provider,
 * mounted once in App.tsx inside the sign-in gate. It wears Artwork's own look (the `artwork`
 * preset, light or dark with the app), keys the project context by the Clerk workspace, and links
 * a project to its files in Artwork (`/?project=<id>`) and to the Projects app (`/overview/:id`, a
 * new tab). Project settings offer the project's file covers and its Share.
 */
export function ArtworkProjects({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { user } = useUser();
  const { theme } = useTheme();
  const workspaceId = organization?.id ?? null;

  // The users-back id ("Owned by me", "(you)"); the same answer the live rooms wait for.
  const [me, setMe] = useState<string | null>(null);
  const clerkId = user?.id ?? null;
  useEffect(() => {
    let live = true;
    setMe(null);
    if (clerkId)
      myUserId(clerkId).then(
        (id) => live && setMe(id),
        () => undefined,
      );
    return () => {
      live = false;
    };
  }, [clerkId]);

  // One adapter per workspace and person: a new one reads the workspace again.
  const adapter = useMemo(() => artworkProjectsAdapter(me), [me, workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps
  const links = useMemo(
    () => ({
      openProject: (id: string) => navigate({ pathname: '/', search: `?project=${encodeURIComponent(id)}` }),
      openInProjects: (id: string | null) => openTab(id ? projectInProjectsUrl(id) : newProjectInProjectsUrl()),
      newInProjects: (name: string) => openTab(newProjectInProjectsUrl(name)),
    }),
    [navigate],
  );

  return (
    <ProjectsProvider app="artwork" noun="file" adapter={adapter} workspaceId={workspaceId} theme="artwork" mode={theme === 'light' ? 'light' : 'dark'} links={links} settings={SETTINGS}>
      {children}
    </ProjectsProvider>
  );
}
